/**
 * Rule editor + persona manager dialog openers.
 *
 * Extracted from src/taskpane/dialog-host.ts so the new Reformat dialog
 * (which lives in a separate webpack entry) can open these as nested
 * dialogs without pulling in the whole task-pane dialog-host module.
 */
import type { Persona, Rule } from "./types";
import type { AuditRule } from "./audit/types";
import {
  addPersona,
  addUserRule,
  deletePersona,
  loadPersonas,
  updatePersona,
  updateUserRule,
} from "./storage";
import { loadAllRules } from "./registry";
import { loadAllAuditRules } from "./audit/registry";


// ----------------------------------------------------------------------------
// Rule editor
// ----------------------------------------------------------------------------

interface SaveRuleFromDialog {
  type: "save";
  rule: Omit<Rule, "id" | "source">;
  existingId?: string;
}

interface CancelRuleFromDialog {
  type: "cancel";
}

interface ReadyFromDialog {
  type: "ready";
}

type RuleEditorFromDialog = SaveRuleFromDialog | CancelRuleFromDialog | ReadyFromDialog;

interface InitToRuleEditor {
  type: "init";
  rule: Rule | null;
}

/**
 * Open the rule editor dialog. Resolves with the saved rule, or null if
 * the user cancelled or closed the dialog window.
 */
export function openRuleEditor(existing?: Rule): Promise<Rule | null> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/dialog.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 65, height: 75, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (rule: Rule | null): void => {
          if (resolved) return;
          resolved = true;
          try { dialog.close(); } catch { /* already closed */ }
          resolve(rule);
        };

        const fail = (err: unknown): void => {
          if (resolved) return;
          resolved = true;
          try { dialog.close(); } catch { /* already closed */ }
          reject(err instanceof Error ? err : new Error(String(err)));
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as RuleEditorFromDialog;
              if (msg.type === "ready") {
                const init: InitToRuleEditor = { type: "init", rule: existing ?? null };
                dialog.messageChild(JSON.stringify(init));
                return;
              }
              if (msg.type === "save") {
                let saved: Rule;
                if (msg.existingId) {
                  await updateUserRule(msg.existingId, msg.rule);
                  saved = { id: msg.existingId, source: "user", ...msg.rule };
                } else {
                  saved = await addUserRule(msg.rule);
                }
                finish(saved);
                return;
              }
              if (msg.type === "cancel") {
                finish(null);
                return;
              }
            } catch (err) {
              fail(err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish(null);
        });
      },
    );
  });
}


// ----------------------------------------------------------------------------
// Persona manager
// ----------------------------------------------------------------------------

interface SavePersonaFromDialog {
  type: "save";
  persona: Persona;
  isNew: boolean;
}

interface DeletePersonaFromDialog {
  type: "delete";
  id: string;
}

interface ClosePersonasFromDialog {
  type: "close";
}

type PersonasFromDialog =
  | SavePersonaFromDialog
  | DeletePersonaFromDialog
  | ClosePersonasFromDialog
  | ReadyFromDialog;

interface InitToPersonas {
  type: "init";
  personas: Persona[];
  rules: Rule[];
  auditRules: AuditRule[];
}

interface PersonasUpdatedToPersonas {
  type: "personas-updated";
  personas: Persona[];
}

/**
 * Open the persona manager dialog. Resolves when the user closes it.
 */
export function openPersonaManager(): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/personas.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 70, height: 80, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try { dialog.close(); } catch { /* already closed */ }
          resolve();
        };

        const fail = (err: unknown): void => {
          if (resolved) return;
          resolved = true;
          try { dialog.close(); } catch { /* already closed */ }
          reject(err instanceof Error ? err : new Error(String(err)));
        };

        const sendPersonasUpdate = async (): Promise<void> => {
          const personas = await loadPersonas();
          const update: PersonasUpdatedToPersonas = { type: "personas-updated", personas };
          dialog.messageChild(JSON.stringify(update));
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as PersonasFromDialog;
              if (msg.type === "ready") {
                const [personas, rules, auditRules] = await Promise.all([
                  loadPersonas(),
                  loadAllRules(),
                  loadAllAuditRules(),
                ]);
                const init: InitToPersonas = { type: "init", personas, rules, auditRules };
                dialog.messageChild(JSON.stringify(init));
                return;
              }
              if (msg.type === "save") {
                if (msg.isNew) {
                  await addPersona({
                    name: msg.persona.name,
                    description: msg.persona.description,
                    ruleIds: msg.persona.ruleIds ?? [],
                    auditRuleIds: msg.persona.auditRuleIds ?? [],
                    autoMatch: msg.persona.autoMatch,
                  });
                } else {
                  await updatePersona(msg.persona.id, {
                    name: msg.persona.name,
                    description: msg.persona.description,
                    ruleIds: msg.persona.ruleIds ?? [],
                    auditRuleIds: msg.persona.auditRuleIds ?? [],
                    autoMatch: msg.persona.autoMatch,
                  });
                }
                await sendPersonasUpdate();
                return;
              }
              if (msg.type === "delete") {
                await deletePersona(msg.id);
                await sendPersonasUpdate();
                return;
              }
              if (msg.type === "close") {
                finish();
                return;
              }
            } catch (err) {
              fail(err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      },
    );
  });
}
